import { Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { Bell, Search, User, Plus, MessageSquare, Package, Truck, AlertTriangle, CheckCircle2, DollarSign, Sparkles, Coffee } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { type NotificationKind } from "@/lib/notifications";
import { useAuth } from "@/lib/auth-context";
import { getMyProfile } from "@/lib/profile.functions";
import { listMyNotifications, markNotificationRead } from "@/lib/notifications.functions";
import { getMyOfferCount } from "@/lib/seller-hub.functions";

import { ProfileDrawer } from "@/components/ProfileDrawer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FulfillmentCartBadge } from "@/components/FulfillmentCartBadge";
import { CategoryNav } from "@/components/CategoryNav";
import { useCurrency } from "@/lib/currency";
import logoDark from "@/assets/wtb-logo-dark.png.asset.json";
import logoLight from "@/assets/wtb-logo-light.png.asset.json";

const NOTIF_ICONS: Record<NotificationKind, typeof Bell> = {
  new_offer: Package,
  offer_accepted: CheckCircle2,
  shipped: Truck,
  delivered: CheckCircle2,
  dispute: AlertTriangle,
  release: DollarSign,
  message: MessageSquare,
  system: Sparkles,
};


export function AppShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const currentSearch = useRouterState({ select: (s) => s.location.search as { q?: string } });
  const [q, setQ] = useState<string>(currentSearch.q ?? "");
  useEffect(() => { setQ(currentSearch.q ?? ""); }, [currentSearch.q]);
  
  const [notifOpen, setNotifOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const { display, setDisplay, available } = useCurrency();
  const { user } = useAuth();
  const getMyProfileFn = useServerFn(getMyProfile);
  const { data: myProfile } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: () => getMyProfileFn(),
    enabled: !!user,
    staleTime: 60_000,
  });
  const username = myProfile?.username ?? null;

  const getOfferCountFn = useServerFn(getMyOfferCount);
  const { data: offerCountData } = useQuery({
    queryKey: ["my-offer-count", user?.id],
    queryFn: () => getOfferCountFn(),
    enabled: !!user,
    staleTime: 60_000,
  });
  const hasSellerHistory = (offerCountData?.count ?? 0) > 0;

  const navigate = useNavigate();
  const qc = useQueryClient();
  const listNotifsFn = useServerFn(listMyNotifications);
  const markReadFn = useServerFn(markNotificationRead);
  const { data: notifData } = useQuery({
    queryKey: ["notifications", "header", user?.id ?? null],
    queryFn: () => listNotifsFn(),
    enabled: !!user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
  const notifications = notifData?.notifications ?? [];
  const unreadCount = notifications.filter((n) => !n.read_at).length;
  const unreadNotifs = unreadCount > 99 ? 99 : unreadCount;
  const unreadNotifsLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const recentNotifs = notifications.slice(0, 6);
  const unreadMsgs = 0;

  async function handleNotifClick(n: { id: string; read_at: string | null; link: string | null }) {
    setNotifOpen(false);
    if (!n.read_at) {
      try {
        await markReadFn({ data: { id: n.id } });
        qc.invalidateQueries({ queryKey: ["notifications", "header"] });
      } catch {
        /* non-fatal */
      }
    }
    if (!n.link) return;
    // External URLs (e.g. carrier tracking) open in a new tab; internal
    // paths use router.history.push so raw resolved URLs like /orders/<uuid>
    // navigate correctly (typed `navigate({to})` expects route patterns).
    if (/^https?:\/\//i.test(n.link)) {
      window.open(n.link, "_blank", "noopener,noreferrer");
    } else {
      router.history.push(n.link);
    }
  }
  // suppress unused-var lint for navigate (kept in case future callers need typed nav)
  void navigate;

  function formatRelative(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  }


  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!notifRef.current?.contains(e.target as Node)) setNotifOpen(false);
    }
    if (notifOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [notifOpen]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-2 px-4 sm:gap-3 sm:px-6">
          {/* 1. Logo */}
          <Link to="/" aria-label="Want To Buy home" className="flex items-center gap-2 shrink-0">
            <img
              src={logoDark.url}
              alt="Want To Buy"
              className="hidden h-10 w-10 rounded-md object-cover dark:block"
            />
            <img
              src={logoLight.url}
              alt="Want To Buy"
              className="block h-10 w-10 rounded-md object-cover dark:hidden"
            />
            <span className="hidden font-display text-lg font-bold tracking-tight sm:block">
              Want To Buy
            </span>
          </Link>

          {/* 2. Light/dark toggle */}
          <ThemeToggle />

          {/* 3. Search */}
          <form
            className="relative flex-1 max-w-xl"
            onSubmit={(e) => {
              e.preventDefault();
              const term = q.trim();
              router.navigate({ to: "/", search: term ? { q: term } : {} });
            }}
            role="search"
          >
            <button type="submit" aria-label="Search" className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
              <Search className="h-4 w-4" />
            </button>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search cards, sets, sellers…"
              className="h-10 w-full rounded-md border border-border bg-panel pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </form>

          {/* 4. + Post buy order */}
          <button
            onClick={() => {
              if (!user) {
                router.navigate({ to: "/auth", search: { redirect: "/create-order" } });
              } else {
                router.navigate({ to: "/create-order" });
              }
            }}
            className="btn-primary hidden h-10 items-center gap-1.5 px-3.5 text-sm sm:inline-flex"
          >
            <Plus className="h-4 w-4" /> Post Buy Order
          </button>

          {/* 5. Currency selector (desktop only) */}
          <select
            value={display}
            onChange={(e) => setDisplay(e.target.value)}
            aria-label="Display currency"
            title="Display only — buyer and seller arrange payment directly"
            className="hidden h-10 rounded-md border border-border bg-panel px-2 text-xs font-semibold focus:border-primary focus:outline-none md:block"
          >
            {available.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* 6. Fulfillment cart (signed-in only) */}
          {user && <FulfillmentCartBadge />}

          {/* 6b. Messages (signed-in only) */}
          {user && (
            <Link
              to="/messages"
              aria-label="Messages"
              className="relative grid h-10 w-10 place-items-center rounded-md border border-border bg-panel hover:bg-muted"
            >
              <MessageSquare className="h-4 w-4" />
              {unreadMsgs > 0 && (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                  {unreadMsgs}
                </span>
              )}
            </Link>
          )}

          {/* 7. Notifications (signed-in only) */}
          {user && (
            <div ref={notifRef} className="relative">
              <button
                onClick={() => setNotifOpen((o) => !o)}
                aria-label="Notifications"
                className="relative grid h-10 w-10 place-items-center rounded-md border border-border bg-panel hover:bg-muted"
              >
                <Bell className="h-4 w-4" />
                {unreadNotifs > 0 && (
                  <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                    {unreadNotifsLabel}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-12 z-50 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border bg-panel shadow-2xl">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div className="font-display text-sm font-bold">Notifications</div>
                    <Link
                      to="/notifications"
                      onClick={() => setNotifOpen(false)}
                      className="text-xs text-primary hover:underline"
                    >
                      View all
                    </Link>
                  </div>
                  {recentNotifs.length === 0 ? (
                    <div className="max-h-[26rem] overflow-y-auto p-6 text-center text-xs text-muted-foreground">
                      No notifications yet — activity on your orders and offers will appear here.
                    </div>
                  ) : (
                    <ul className="max-h-[26rem] divide-y divide-border overflow-y-auto">
                      {recentNotifs.map((n) => (
                        <li key={n.id}>
                          <button
                            type="button"
                            onClick={() => handleNotifClick(n)}
                            className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 ${
                              n.read_at ? "" : "bg-primary/5"
                            }`}
                          >
                            <span
                              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                                n.read_at ? "bg-transparent" : "bg-primary"
                              }`}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-xs font-semibold">{n.title}</span>
                              {n.body && (
                                <span className="mt-0.5 block line-clamp-2 text-[11px] text-muted-foreground">
                                  {n.body}
                                </span>
                              )}
                              <span className="mt-1 block text-[10px] uppercase tracking-wider text-muted-foreground/70">
                                {formatRelative(n.created_at)}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link
                    to="/notifications"
                    onClick={() => setNotifOpen(false)}
                    className="block border-t border-border py-2.5 text-center text-xs font-semibold text-primary hover:bg-muted/40"
                  >
                    See all notifications
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* 8. Profile (opens drawer) — or Sign in when signed out */}
          {user ? (
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open account menu"
              className="grid h-10 w-10 place-items-center rounded-md border border-border bg-panel hover:bg-muted"
            >
              <User className="h-4 w-4" />
            </button>
          ) : (
            <Link
              to="/auth"
              className="btn-primary inline-flex h-10 items-center gap-1.5 px-3.5 text-sm"
            >
              Sign in
            </Link>
          )}

        </div>

        {/* Secondary nav */}
        <div className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-4 pb-2 text-sm sm:px-6">
          {([
            { to: "/", label: "Browse", requiresAuth: false, show: true },
            { to: "/verified-stores", label: "Verified Stores", requiresAuth: false, show: true },
          ] as const)


            .filter((l) => (!l.requiresAuth || !!user) && l.show)
            .map((l) => (
              <Link
                key={l.to}
                to={l.to as any}
                className="rounded-md px-3 py-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                activeProps={{ className: "rounded-md px-3 py-1.5 bg-muted text-foreground" }}
                activeOptions={{ exact: l.to === "/" }}
              >
                {l.label}
              </Link>
            ))}
        </div>
      </header>

      <CategoryNav />

      <ProfileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} username={username} />

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10">{children}</main>

      <footer className="mt-16 border-t border-border bg-panel/40">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-2">
                <img src={logoDark.url} alt="Want To Buy" className="hidden h-8 w-8 rounded object-cover dark:block" />
                <img src={logoLight.url} alt="Want To Buy" className="block h-8 w-8 rounded object-cover dark:hidden" />
                <span className="font-display text-base font-bold">Want To Buy</span>
              </div>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">For Collectors, By Collectors</p>
              <p className="mt-3 text-xs text-muted-foreground max-w-xs">
                Want To Buy connects TCG buyers with sellers who have the cards they're looking for. Payments and shipping happen directly between you and the seller.
              </p>
            </div>
            <FooterCol title="Marketplace" links={[
              { to: "/", label: "Browse orders" },
              { to: "/create-order", label: "Post a buy order" },
              { to: "/dashboard", label: "Dashboard" },
              { to: "/wishlist", label: "Watchlist" },
            ]} />
            <FooterCol title="Policies" links={[
              { href: "/fees", label: "Fees" },
              { href: "/terms", label: "Terms & Conditions" },
              { href: "/platform-policies", label: "Platform Policies" },
              { href: "/seller-policy", label: "Seller Policy" },
              { href: "/condition-guide", label: "Condition Guide" },
              { href: "/policies", label: "All policies" },
            ]} />
            <FooterCol title="Trust & Safety" links={[
              { href: "/payment-safety", label: "Payment Safety" },
              { href: "/verified-stores", label: "Verified Stores" },
              { href: "/banned-accounts", label: "Banned accounts" },
              { href: "/rating-policy", label: "Rating & Strike Policy" },
            ]} />

            <FooterCol title="Company" links={[
              { href: "/about", label: "About" },
              { href: "/faq", label: "FAQ" },
              { href: "/contact", label: "Contact & Support" },
            ]} />
          </div>
          {import.meta.env.VITE_KOFI_URL && (
            <div className="mt-8 border-t border-border pt-6 text-center">
              <p className="mx-auto max-w-xl text-xs leading-relaxed text-muted-foreground">
                Want To Buy is an independent platform built by one TCG collector. No corporate backing — just a tool for the community. If it's helped you, consider buying me a coffee to keep it running ☕
              </p>
              <a
                href={import.meta.env.VITE_KOFI_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Support Want To Buy on Ko-fi"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:bg-primary/85"
              >
                <Coffee className="h-3.5 w-3.5" /> Buy me a coffee
              </a>
            </div>
          )}

          <div className="mt-8 border-t border-border pt-6 text-center text-xs text-muted-foreground">
            Want To Buy — buyer-first TCG connection platform · Prices shown in USD; buyer and seller arrange payment directly via PayPal Goods &amp; Services.
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }: { title: string; links: { to?: string; href?: string; label: string }[] }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      <ul className="mt-3 space-y-2 text-sm">
        {links.map((l) =>
          l.to ? (
            <li key={l.label}><Link to={l.to as any} className="text-foreground hover:text-primary">{l.label}</Link></li>
          ) : (
            <li key={l.label}><a href={l.href} className="text-foreground hover:text-primary">{l.label}</a></li>
          )
        )}
      </ul>
    </div>
  );
}
